import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { DEV_NEW_VERSION_KEY, useAppMessages } from '@components/appMessages/AppMessagesProvider.tsx';

/**
 * "A new version is available" — an overlay the user has to resolve by reloading.
 *
 * Deliberately has no dismiss control: the point is that the user cannot end up working on an old
 * bundle. It overlays rather than pushes so it can appear mid-task without moving the page under
 * the user's cursor, and only the bar itself takes pointer events, so whatever they are doing —
 * a sidebar edit, creating an entity — can still be finished underneath.
 */
const NewVersionBanner: React.FC = () => {
  const { newVersion } = useAppMessages();
  const { tr } = useTranslationLgs();

  if (!newVersion) return null;

  return (
    <div
      // Above PrimeReact overlays (sidebars/dialogs run at baseZIndex 1000000) so it stays visible
      // while a panel is open. Offset below the maintenance strip, which keeps the true page top.
      className={'fixed left-0 right-0 flex items-center justify-between gap-4 p-3 bg-complementary-warning shadow-md'}
      style={{ top: 'var(--app-maintenance-height, 0px)', zIndex: 1000100 }}
      role="status"
    >
      <div className={'text-content-primary'}>
        {tr(`TopInformation.versionDetected`, `Version detected...`)}
        {newVersion.commit_message ? ` - ${newVersion.commit_message}` : ''}
      </div>
      <RingilButton
        label={tr('TopInformation.reload', 'Reload')}
        mode={'filled'}
        onClick={() => {
          // Clear the simulation flag as well, so a simulated banner is genuinely resolved by
          // Reload instead of reappearing two seconds later. A no-op when the flag is not set.
          try {
            localStorage.removeItem(DEV_NEW_VERSION_KEY);
          } catch {
            /* storage unavailable — nothing to clear */
          }
          location.reload();
        }}
        dataTestId={'newVersionReload'}
      />
    </div>
  );
};

export default NewVersionBanner;
